import { Component, ComponentType } from '@/types';
import { resolvePreferredTimeZone } from './dateTimeFormatting';
import type { ChartMeasure } from '@/types/chartDataSource';

/**
 * Component types that support data connections
 */
const DATA_CAPABLE_TYPES = new Set<ComponentType>([
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.AREA_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.MAP_CHART,
  ComponentType.DATA_TEXT,
  ComponentType.DATA_CARD,
  ComponentType.FERMENTATION_TANK_CARD,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.KANBAN_BOARD,
  ComponentType.GANTT_CHART,
  ComponentType.CALENDAR,
  ComponentType.RADIAL_DIAL,
  ComponentType.TOGGLE_SWITCH,
  ComponentType.SLIDER,
  ComponentType.STEPPER,
  ComponentType.LEVEL_INDICATOR,
  ComponentType.STATUS_LIGHT,
  ComponentType.THERMOSTAT,
  ComponentType.CHECKLIST,
  ComponentType.PROJECT_HUB,
  ComponentType.QUESTIONNAIRE,
  ComponentType.VOTING_CARD,
  ComponentType.TIME_TRACKER,
]);

export interface DataConnectionInfo {
  schemaName: string;
  measures: string[];
  refreshInterval: number | undefined;
  sourceType: string;
  valueField?: string;
  /** Set for composite-query sources */
  sourceCount?: number;
  /** Join strategy label for composite-query sources */
  joinType?: string;
}

/**
 * Returns true if the component is a data-capable type
 */
export const isDataCapableType = (type: ComponentType): boolean => {
  return DATA_CAPABLE_TYPES.has(type);
};

/**
 * Returns true if the component has an active data connection
 */
export const hasDataConnection = (component: Component): boolean => {
  if (!DATA_CAPABLE_TYPES.has(component.type)) return false;
  const props = component.properties as Record<string, unknown>;
  const dataSource = props?.dataSource as Record<string, unknown> | undefined;
  if (dataSource?.type === 'query-template' && dataSource?.queryDefinition) return true;
  if (dataSource?.type === 'timestamp-aggregate' && dataSource?.queryDefinition) return true;
  if (dataSource?.type === 'composite-query' && dataSource?.compositeQueryDefinition) return true;
  if (props?.queryTemplateId) return true;
  return false;
};

/**
 * Extracts data connection summary info for tooltip display
 */
export const getDataConnectionInfo = (component: Component): DataConnectionInfo | null => {
  if (!hasDataConnection(component)) return null;
  const props = component.properties as Record<string, unknown>;
  const dataSource = props?.dataSource as Record<string, unknown> | undefined;

  // Composite query: show source count and join strategy instead of single schema info
  if (dataSource?.type === 'composite-query') {
    const compositeDef = dataSource.compositeQueryDefinition as Record<string, unknown> | undefined;
    const sources = (compositeDef?.sources as unknown[]) ?? [];
    const joinType = (compositeDef?.joinType as string) ?? 'unknown';
    return {
      schemaName: `${sources.length} source${sources.length !== 1 ? 's' : ''}`,
      measures: [],
      refreshInterval: dataSource.refreshInterval as number | undefined,
      sourceType: 'Composite Query',
      sourceCount: sources.length,
      joinType,
    };
  }

  const queryDef = dataSource?.queryDefinition as Record<string, unknown> | undefined;
  const schemaName = (queryDef?.schemaName as string) || 'Unknown';
  const rawMeasures = (queryDef?.measures as ChartMeasure[]) || [];
  const measures = rawMeasures.slice(0, 3).map(m => m.alias || m.field);
  if (rawMeasures.length > 3) {
    measures.push(`+${rawMeasures.length - 3} more`);
  }

  const refreshInterval = dataSource?.refreshInterval as number | undefined;

  let sourceType = 'Query Template';
  if (dataSource?.type === 'static') sourceType = 'Static';
  else if (dataSource?.type === 'timestamp-aggregate') sourceType = 'Aggregate';

  const result: DataConnectionInfo = { schemaName, measures, refreshInterval, sourceType };

  // For DATA_TEXT/DATA_CARD, include valueField
  if (
    component.type === ComponentType.DATA_TEXT ||
    component.type === ComponentType.DATA_CARD
  ) {
    const valueField = props?.valueField as string | undefined;
    if (valueField) result.valueField = valueField;
  }

  return result;
};

/**
 * Formats a refresh interval in seconds to human-readable form
 */
export const formatRefreshInterval = (seconds: number | undefined): string => {
  if (!seconds || seconds <= 0) return 'Manual';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

/**
 * Internal properties that should never be shown in tooltips
 */
const INTERNAL_PROPERTIES = new Set([
  'id',
  'projectId',
  'pageId',
  'createdBy',
  'lastModifiedBy',
  'lastModifiedAt',
  'version',
  'isDeleted',
  'deletedAt',
  'createdAt',
  'updatedAt',
]);

/**
 * Formats a component property value for display
 */
export const formatPropertyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'number') {
    // Format numbers with up to 2 decimal places
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  if (typeof value === 'string') {
    // Truncate long strings
    if (value.length > 50) {
      return `${value.substring(0, 47)}...`;
    }
    return value;
  }

  if (Array.isArray(value)) {
    return `Array(${value.length})`;
  }

  if (value !== null && typeof value === 'object') {
    return 'Object';
  }

  return String(value);
};

/**
 * Formats a style property value for display with unit handling
 */
export const formatStyleValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'number') {
    // Add px unit for size-related properties
    const sizeProperties = [
      'width',
      'height',
      'padding',
      'margin',
      'fontSize',
      'lineHeight',
      'borderRadius',
      'borderWidth',
      'gap',
      'top',
      'right',
      'bottom',
      'left',
    ];

    if (sizeProperties.some(prop => key.toLowerCase().includes(prop))) {
      return `${value}px`;
    }

    return value.toString();
  }

  return formatPropertyValue(value);
};

/**
 * Gets the most relevant properties to display for a specific component type
 */
export const getRelevantProperties = (component: Component): Record<string, unknown> => {
  const { type, properties } = component;
  const relevant: Record<string, unknown> = {};

  // Filter out internal properties
  const filteredProps = Object.entries(properties).filter(
    ([key]) => !INTERNAL_PROPERTIES.has(key)
  );

  switch (type) {
    case ComponentType.TEXT:
      // Text component: content, typography, alignment
      const textProps = ['content', 'fontSize', 'fontWeight', 'textAlign', 'color', 'lineHeight'];
      textProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      break;

    case ComponentType.BUTTON:
      // Button: text, variant, state, navigation
      const buttonProps = ['text', 'variant', 'disabled'];
      buttonProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });

      // Add navigation information if button has action
      if (properties.action === 'navigate' && properties.target) {
        relevant.action = 'Navigate to Page';
        relevant.targetPage = properties.target; // Page ID (will be shown as-is for now)
        // If navigationLabel exists, show it as the target page name
        if (properties.navigationLabel) {
          relevant.targetPage = properties.navigationLabel;
        }
      } else if (properties.action) {
        relevant.action = properties.action;
      }
      break;

    case ComponentType.INPUT:
      // Input: placeholder, type, value, validation
      const inputProps = ['placeholder', 'type', 'value', 'required', 'disabled'];
      inputProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      break;

    case ComponentType.IMAGE:
      // Image: src, alt, objectFit
      const imageProps = ['src', 'alt', 'objectFit'];
      imageProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      break;

    case ComponentType.FLEX_CONTAINER:
    case ComponentType.CONTAINER:
      // Container: flex properties, layout
      const containerProps = [
        'flexDirection',
        'justifyContent',
        'alignItems',
        'gap',
        'padding',
        'backgroundColor',
        'borderRadius',
        'minHeight',
      ];
      containerProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      // Add children count
      if (component.children.length > 0) {
        relevant.childrenCount = component.children.length;
      }
      break;

    case ComponentType.CHART:
    case ComponentType.BAR_CHART:
    case ComponentType.LINE_CHART:
    case ComponentType.PIE_CHART:
    case ComponentType.SCATTER_CHART:
    case ComponentType.AREA_CHART:
    case ComponentType.HEATMAP_CHART:
    case ComponentType.TREEMAP_CHART:
    case ComponentType.GAUGE_CHART:
    case ComponentType.VERTICAL_GAUGE_CHART:
    case ComponentType.MAP_CHART:
      // Charts: title, chart type, display options (dataSource excluded — shown in DATA section)
      const chartProps = ['title', 'chartType', 'showLegend', 'showGrid'];
      chartProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      break;

    case ComponentType.DATA_TEXT:
    case ComponentType.DATA_CARD:
    case ComponentType.FERMENTATION_TANK_CARD:
      // Data components: display props (dataSource excluded — shown in DATA section)
      const dataProps = ['title', 'label', 'formatter', 'unit', 'valueField'];
      dataProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      break;

    case ComponentType.TABLE:
    case ComponentType.ADVANCED_TABLE:
      // Tables: columns, pagination (dataSource excluded — shown in DATA section)
      const tableProps = ['columns', 'pagination', 'sortable', 'filterable'];
      tableProps.forEach(key => {
        if (properties[key] !== undefined) {
          relevant[key] = properties[key];
        }
      });
      break;

    default:
      // For other types, show first 8 non-internal properties
      filteredProps.slice(0, 8).forEach(([key, value]) => {
        relevant[key] = value;
      });
  }

  return relevant;
};

/**
 * Gets the most relevant styles to display (top 8)
 */
export const getRelevantStyles = (component: Component): Record<string, unknown> => {
  const { style } = component;
  const relevant: Record<string, unknown> = {};

  // Guard against undefined or null style
  if (!style) {
    return relevant;
  }

  // Priority styles to show first
  const priorityStyles = [
    'backgroundColor',
    'color',
    'fontSize',
    'padding',
    'margin',
    'border',
    'borderRadius',
    'display',
    'flexDirection',
    'justifyContent',
    'alignItems',
    'width',
    'height',
  ];

  // Add priority styles that exist
  priorityStyles.forEach(key => {
    if (style[key] !== undefined && style[key] !== null) {
      relevant[key] = style[key];
    }
  });

  // Add other non-default styles up to 8 total
  const otherStyles = Object.entries(style).filter(
    ([key, value]) =>
      !priorityStyles.includes(key) &&
      value !== undefined &&
      value !== null &&
      value !== ''
  );

  const remaining = 8 - Object.keys(relevant).length;
  otherStyles.slice(0, remaining).forEach(([key, value]) => {
    relevant[key] = value;
  });

  return relevant;
};

/**
 * Gets a user-friendly component type label with icon
 */
export const getComponentTypeLabel = (type: ComponentType): { icon: string; label: string } => {
  const typeMap: Record<ComponentType, { icon: string; label: string }> = {
    [ComponentType.TEXT]: { icon: '📝', label: 'Text' },
    [ComponentType.DATA_TEXT]: { icon: '📊', label: 'Data Text' },
    [ComponentType.CONTAINER]: { icon: '📦', label: 'Container' },
    [ComponentType.FLEX_CONTAINER]: { icon: '🔷', label: 'Flex Container' },
    [ComponentType.INPUT]: { icon: '✏️', label: 'Input' },
    [ComponentType.BUTTON]: { icon: '🔘', label: 'Button' },
    [ComponentType.IMAGE]: { icon: '🖼️', label: 'Image' },
    [ComponentType.ICON]: { icon: '🎨', label: 'Icon' },
    [ComponentType.CHART]: { icon: '📊', label: 'Chart' },
    [ComponentType.BAR_CHART]: { icon: '📊', label: 'Bar Chart' },
    [ComponentType.LINE_CHART]: { icon: '📈', label: 'Line Chart' },
    [ComponentType.PIE_CHART]: { icon: '🥧', label: 'Pie Chart' },
    [ComponentType.SCATTER_CHART]: { icon: '⚫', label: 'Scatter Chart' },
    [ComponentType.AREA_CHART]: { icon: '🏔️', label: 'Area Chart' },
    [ComponentType.HEATMAP_CHART]: { icon: '🔥', label: 'Heatmap' },
    [ComponentType.TREEMAP_CHART]: { icon: '🌳', label: 'Treemap' },
    [ComponentType.GAUGE_CHART]: { icon: '⏱️', label: 'Gauge' },
    [ComponentType.VERTICAL_GAUGE_CHART]: { icon: '📏', label: 'Vertical Gauge' },
    [ComponentType.DATA_CARD]: { icon: '💳', label: 'Data Card' },
    [ComponentType.FERMENTATION_TANK_CARD]: { icon: '🍺', label: 'Tank Card' },
    [ComponentType.TABLE]: { icon: '📋', label: 'Table' },
    [ComponentType.ADVANCED_TABLE]: { icon: '📊', label: 'Advanced Table' },
    [ComponentType.LIST]: { icon: '📄', label: 'List' },
    [ComponentType.CUSTOM]: { icon: '⚙️', label: 'Custom' },
    [ComponentType.CARD]: { icon: '🃏', label: 'Card' },
    [ComponentType.MAP_CHART]: { icon: '🗺️', label: 'Map' },
    [ComponentType.FORM_CONTAINER]: { icon: '📋', label: 'Form' },
    [ComponentType.BREADCRUMB]: { icon: '🔗', label: 'Breadcrumb' },
    [ComponentType.FILE_STORAGE]: { icon: '📁', label: 'File Storage' },
    [ComponentType.KANBAN_BOARD]: { icon: '📋', label: 'Kanban Board' },
    [ComponentType.RADIAL_DIAL]: { icon: '◉', label: 'Radial Dial' },
    [ComponentType.TOGGLE_SWITCH]: { icon: '⏻', label: 'Toggle Switch' },
    [ComponentType.PUSH_BUTTON]: { icon: '⏺', label: 'Push Button' },
    [ComponentType.ICON_BUTTON]: { icon: '⊡', label: 'Icon Button' },
    [ComponentType.STATUS_LIGHT]: { icon: '●', label: 'Status Light' },
    [ComponentType.SLIDER]: { icon: '━', label: 'Slider' },
    [ComponentType.STEPPER]: { icon: '±', label: 'Stepper' },
    [ComponentType.LEVEL_INDICATOR]: { icon: '▮', label: 'Level Indicator' },
    [ComponentType.SEGMENTED_CONTROL]: { icon: '▦', label: 'Segmented Control' },
    [ComponentType.COLOR_PICKER]: { icon: '◔', label: 'Color Picker' },
    [ComponentType.THERMOSTAT]: { icon: '◎', label: 'Thermostat' },
    [ComponentType.MODAL]: { icon: '⬜', label: 'Modal' },
    [ComponentType.CHECKLIST]: { icon: '☑️', label: 'Checklist' },
    [ComponentType.HORIZONTAL_TIMELINE]: { icon: '📅', label: 'Timeline' },
    [ComponentType.ASSIGNMENT_WIDGET]: { icon: '👥', label: 'Assignments' },
    [ComponentType.GANTT_CHART]: { icon: '📊', label: 'Gantt Chart' },
    [ComponentType.CALENDAR]: { icon: '📅', label: 'Calendar' },
    [ComponentType.PROJECT_HUB]: { icon: '🗂️', label: 'Project Hub' },
    [ComponentType.QUESTIONNAIRE]: { icon: '📋', label: 'Questionnaire' },
    [ComponentType.VOTING_CARD]: { icon: '🗳️', label: 'Voting Card' },
    [ComponentType.TIME_TRACKER]: { icon: '⏱️', label: 'Time Tracker' },
  };

  return typeMap[type] || { icon: '❓', label: type };
};

/**
 * Truncates component ID for display
 */
export const truncateId = (id: string, maxLength: number = 12): string => {
  if (id.length <= maxLength) {
    return id;
  }
  return `${id.substring(0, maxLength)}...`;
};

/**
 * Formats positioning strategy for display
 */
export const formatPositioningStrategy = (strategy: string): string => {
  const strategyMap: Record<string, string> = {
    absolute: 'Absolute',
    relative: 'Relative',
    flex: 'Flexbox',
    grid: 'CSS Grid',
    auto: 'Auto',
  };

  return strategyMap[strategy] || strategy;
};

/**
 * Formats a metric value based on the specified formatter type
 */
interface MetricFormattingOptions {
  timeZone?: string;
  decimalPlaces?: number;
  thousandsSeparator?: boolean;
  currencySymbol?: string;
  currencyCode?: string;
}

const buildDateTimeOptions = (
  options: Intl.DateTimeFormatOptions,
  timeZone?: string
): Intl.DateTimeFormatOptions => ({
  ...options,
  ...(timeZone ? { timeZone } : {}),
});

export const formatMetricValue = (
  value: number | string,
  formatter: string = 'number',
  options?: MetricFormattingOptions
): string => {
  const resolvedTimeZone = resolvePreferredTimeZone(options?.timeZone);
  const decimalPlaces =
    typeof options?.decimalPlaces === 'number' &&
    Number.isFinite(options.decimalPlaces) &&
    options.decimalPlaces >= 0
      ? Math.min(20, Math.floor(options.decimalPlaces))
      : undefined;
  const useGrouping = options?.thousandsSeparator ?? true;
  const fractionDigitOptions =
    decimalPlaces !== undefined
      ? {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        }
      : {};

  // Handle date/datetime formatters for both string and number values
  if (formatter === 'date') {
    // Handle Unix timestamps (numbers)
    if (typeof value === 'number') {
      return new Intl.DateTimeFormat('en-US', {
        ...buildDateTimeOptions({
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }, resolvedTimeZone),
      }).format(new Date(value));
    }
    // Handle ISO date strings
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!isNaN(parsed)) {
        return new Intl.DateTimeFormat('en-US', {
          ...buildDateTimeOptions({
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }, resolvedTimeZone),
        }).format(new Date(parsed));
      }
    }
    // Pass through text labels as-is (e.g., "Jan", "Q1")
    return String(value);
  }

  if (formatter === 'time') {
    if (typeof value === 'number') {
      return new Intl.DateTimeFormat('en-US', {
        ...buildDateTimeOptions({
          hour: 'numeric',
          minute: '2-digit',
        }, resolvedTimeZone),
      }).format(new Date(value));
    }
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!isNaN(parsed)) {
        return new Intl.DateTimeFormat('en-US', {
          ...buildDateTimeOptions({
            hour: 'numeric',
            minute: '2-digit',
          }, resolvedTimeZone),
        }).format(new Date(parsed));
      }
    }
    return String(value);
  }

  if (formatter === 'datetime') {
    // Handle Unix timestamps (numbers)
    if (typeof value === 'number') {
      return new Intl.DateTimeFormat('en-US', {
        ...buildDateTimeOptions({
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }, resolvedTimeZone),
      }).format(new Date(value));
    }
    // Handle ISO date strings
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!isNaN(parsed)) {
        return new Intl.DateTimeFormat('en-US', {
          ...buildDateTimeOptions({
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }, resolvedTimeZone),
        }).format(new Date(parsed));
      }
    }
    // Pass through text labels as-is
    return String(value);
  }

  // For non-date formatters, string values pass through
  if (typeof value === 'string') {
    return value;
  }

  const formatNumber = (
    numericValue: number,
    numberOptions: Intl.NumberFormatOptions = {}
  ): string =>
    new Intl.NumberFormat('en-US', {
      useGrouping,
      ...fractionDigitOptions,
      ...numberOptions,
    }).format(numericValue);

  const symbolToCurrencyCode: Record<string, string> = {
    '$': 'USD',
    '€': 'EUR',
    '£': 'GBP',
    '¥': 'JPY',
  };

  switch (formatter) {
    case 'currency': {
      const currencyCode =
        options?.currencyCode ||
        (options?.currencySymbol
          ? symbolToCurrencyCode[options.currencySymbol]
          : undefined);

      if (currencyCode) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          useGrouping,
          ...(decimalPlaces !== undefined
            ? fractionDigitOptions
            : { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        }).format(value);
      }

      if (options?.currencySymbol) {
        const defaultCurrencyDigits =
          decimalPlaces === undefined
            ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
            : {};
        return `${options.currencySymbol}${formatNumber(value, defaultCurrencyDigits)}`;
      }

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        useGrouping,
        ...(decimalPlaces !== undefined
          ? fractionDigitOptions
          : { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
      }).format(value);
    }

    case 'percent':
      return `${formatNumber(value)}%`;

    case 'decimal':
      return formatNumber(
        value,
        decimalPlaces === undefined
          ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          : {}
      );

    case 'integer':
      return formatNumber(Math.round(value), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

    case 'compact':
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        useGrouping,
        ...fractionDigitOptions,
      }).format(value);

    case 'number':
    default:
      return formatNumber(value);
  }
};

/**
 * Formats a chart axis tick value, auto-detecting ISO dates
 * Uses a compact format suitable for axis labels
 */
export const formatAxisTick = (value: unknown, options?: MetricFormattingOptions): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const resolvedTimeZone = resolvePreferredTimeZone(options?.timeZone);

  // Check for ISO 8601 date strings
  if (typeof value === 'string') {
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoDatePattern.test(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Use compact format for axis: "Jan 26, 11:37 PM"
        return new Intl.DateTimeFormat('en-US', {
          ...buildDateTimeOptions({
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }, resolvedTimeZone),
        }).format(date);
      }
    }
    return value;
  }

  // Handle Unix timestamps (large numbers that could be milliseconds since epoch)
  if (typeof value === 'number') {
    // If it looks like a timestamp (year 2000+ in milliseconds)
    if (value > 946684800000 && value < 4102444800000) {
      const date = new Date(value);
      return new Intl.DateTimeFormat('en-US', {
        ...buildDateTimeOptions({
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }, resolvedTimeZone),
      }).format(date);
    }
    return String(value);
  }

  return String(value);
};

/**
 * Formats numeric Y-axis ticks into readable values without exposing
 * long floating-point labels like 92.6666667.
 */
export const formatNumericAxisTick = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return formatAxisTick(value);
  }

  const absoluteValue = Math.abs(value);
  const maximumFractionDigits =
    absoluteValue >= 100 ? 0 :
    absoluteValue >= 10 ? 1 :
    absoluteValue >= 1 ? 2 :
    absoluteValue >= 0.1 ? 3 :
    absoluteValue >= 0.01 ? 4 :
    5;

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
  }).format(value);
};
